import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { irAGenerados, postGenerarConRecuperacion } from "@/lib/generarDeca";
import { useNavigate } from "react-router-dom";
import { Field, PageHeader, KV } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle2, ChevronLeft, Loader2, ShieldAlert, Truck, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { EnviosSortableTable, claveEnvioAlb } from "@/components/SortableTh";
import ConductorSelect from "@/components/ConductorSelect";
import MatriculaSelect from "@/components/MatriculaSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { fechaTransportePorDefecto, fmtFecha } from "@/lib/fechas";
import { fmtAlbaranNro, palabraEnvio } from "@/lib/textos";
import AvisoSalida, { useAvisoSalida } from "@/components/AvisoSalida";
import ConfirmarBorrador from "@/components/ConfirmarBorrador";
import { LEN } from "@/lib/limites";
import DestinoClienteSelect, { fmtDomicilio } from "@/components/DestinoClienteSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/auth/AuthContext";

const CAMPO_ART6 = {
  "cargador.razon_social": "Raz" + String.fromCharCode(0xf3) + "n social del cargador",
  "cargador.nif": "NIF / CIF / NIE del cargador",
  "transportista.razon_social": "Raz" + String.fromCharCode(0xf3) + "n social del transportista",
  "transportista.nif": "NIF / CIF / NIE del transportista",
  origen: "Origen (calle, CP, municipio y provincia)",
  destino: "Destino",
  naturaleza: "Naturaleza de la mercanc" + String.fromCharCode(0xed) + "a",
  peso_bruto: "Peso bruto (> 0)",
  bultos: "Bultos (> 0)",
  fecha_transporte: "Fecha de transporte",
  "destinatario.nif": "NIF / CIF / NIE / VAT del destinatario",
  "destinatario.cp": "C" + String.fromCharCode(0xf3) + "digo postal del destinatario",
  "conductor.dni": "Documento del conductor",
  matriculas: "Matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo",
  "matriculas.vehiculo": "Matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo",
  "matriculas.remolque": "Matr" + String.fromCharCode(0xed) + "cula del remolque",
  utilizado_en_deca: "Ya est" + String.fromCharCode(0xe1) + " en otro DeCA (un albar" + String.fromCharCode(0xe1) + "n solo puede usarse una vez)",
};

const AVISO_REUTILIZAR = "Ya est" + String.fromCharCode(0xe1) + " en otro DeCA. Puedes reutilizarlo, pero revisa que no sea un error.";

function etiquetaCampo(codigo) {
  return CAMPO_ART6[codigo] || codigo;
}

function claveTraSelect(t) {
  const nif = String(t?.nif || "").trim().toUpperCase();
  if (nif) return "nif:" + nif;
  const cod = t?.codigo != null && t.codigo !== "" ? String(t.codigo) : "";
  if (cod && cod !== "0") return "cod:" + cod;
  const nom = String(t?.razon_social || "").trim().toLowerCase();
  return nom ? "nom:" + nom : "";
}

function mergeTraCatalogo(base, extras) {
  const out = [];
  const seen = new Set();
  for (const t of [...(extras || []), ...(base || [])]) {
    if (!t || typeof t !== "object") continue;
    const k = claveTraSelect(t);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

function etiquetaTraSelect(t) {
  const nom = (t?.razon_social || "").trim() || (t?.codigo != null && t.codigo !== "" ? "C" + String.fromCharCode(0xf3) + "d. " + t.codigo : "");
  const nif = (t?.nif || "").trim();
  if (nif) return nom + " (" + nif + ")";
  if (t?.codigo != null && t.codigo !== "") return nom + " (C" + String.fromCharCode(0xf3) + "d. " + t.codigo + ")";
  return nom;
}

function etiquetaRef(a) {
  const ser = (a?.serie || "").trim();
  const n = a?.numero;
  const ej = a?.ejercicio;
  if (ej && ser) return `${ej}/${ser}-${n}`;
  if (ej) return `${ej}/${n}`;
  return ser ? `${ser}-${n}` : String(n ?? "");
}

function matriculasPorNif(vehiculos, remolques) {
  const keys = new Set([...Object.keys(vehiculos), ...Object.keys(remolques)]);
  const out = {};
  for (const k of keys) {
    const arr = [vehiculos[k], remolques[k]].map((s) => (s || "").trim()).filter(Boolean);
    if (arr.length) out[k] = arr;
  }
  return out;
}

export default function PreviewPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const sageCon = !!user?.sage_conductores && user?.sage_conectado === true;
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(false);
  const [autorizacion, setAutorizacion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [fecha, setFecha] = useState("");
  const fechaEditada = useRef(false);
  const [fechaDescarga, setFechaDescarga] = useState("");
  const [conductoresPorNif, setConductoresPorNif] = useState({});
  const [vehiculosPorNif, setVehiculosPorNif] = useState({});
  const [remolquesPorNif, setRemolquesPorNif] = useState({});
  const [askBorrador, setAskBorrador] = useState(false);
  const [enviosEdit, setEnviosEdit] = useState({});
  const [traPorGrupo, setTraPorGrupo] = useState({});
  const [catalogoTra, setCatalogoTra] = useState([]);
  const [traLibre, setTraLibre] = useState({});
  const [cargadorContractual, setCargadorContractual] = useState("empresa");
  const { blocker, saliendo, liberar, confirmar, cancelar } = useAvisoSalida(!!payload);
  const puedeElegirCargador = !!user?.cargador_es_cliente && payload?.tipo_deca !== "proveedor";

  useEffect(() => {
    const raw = sessionStorage.getItem("deca_preview_payload");
    if (!raw) { nav("/albaranes"); return; }
    setPayload(JSON.parse(raw));
  }, []); // eslint-disable-line

  const buildOverrides = (extra = {}) => {
    const envios = Object.entries(enviosEdit).map(([k, v]) => {
      const [ejercicio, serie, numero] = k.split("|");
      const bultos = v.bultos === "" || v.bultos == null ? undefined : Number(v.bultos);
      const peso = v.peso_bruto === "" || v.peso_bruto == null ? undefined : Number(v.peso_bruto);
      const ejN = Number(ejercicio);
      const numN = Number(numero);
      const row = {
        ejercicio: Number.isFinite(ejN) ? ejN : 0,
        serie: serie || "",
        numero: Number.isFinite(numN) ? numN : numero,
      };
      if (Number.isFinite(bultos) && bultos > 0) row.bultos = bultos;
      if (Number.isFinite(peso) && peso > 0) row.peso_bruto = peso;
      if (v.dest_id && v.dest_id !== "sage") {
        row.destino = v.destino || "";
        if (v.destinatario_domicilio) row.destinatario_domicilio = v.destinatario_domicilio;
        if (v.destinatario_municipio) row.destinatario_municipio = v.destinatario_municipio;
        if (v.destinatario_cp) row.destinatario_cp = v.destinatario_cp;
        if (v.destinatario_provincia) row.destinatario_provincia = v.destinatario_provincia;
      }
      if (v.orig_id && v.orig_id !== "sage") {
        const ori = (v.origen || "").trim() || fmtDomicilio({
          domicilio: v.orig_domicilio,
          municipio: v.orig_municipio,
          cp: v.orig_cp,
          provincia: v.orig_provincia,
        });
        if (ori) row.origen = ori;
      }
      return row;
    }).filter((e) => e.bultos || e.peso_bruto || e.destino || e.origen);
    const transportistas_por_grupo = {};
    Object.entries(traPorGrupo).forEach(([idx, t]) => {
      if (!t?.nif && !t?.razon_social) return;
      transportistas_por_grupo[idx] = {
        nif: t.nif || "",
        razon_social: t.razon_social || "",
        domicilio: t.domicilio || "",
        municipio: t.municipio || "",
        cp: t.cp || "",
        provincia: t.provincia || "",
        email: t.email || "",
        codigo: t.codigo,
      };
    });
    return {
      autorizacion_especial: autorizacion || undefined,
      observaciones: observaciones || undefined,
      fecha_transporte: fecha || undefined,
      fecha_descarga: fechaDescarga || undefined,
      envios: envios.length ? envios : undefined,
      transportistas_por_grupo: Object.keys(transportistas_por_grupo).length ? transportistas_por_grupo : undefined,
      ...extra,
    };
  };

  const load = async () => {
    if (!payload) return;
    setLoading(true);
    setPreviewError("");
    try {
      const { data } = await api.post("/api/v1/decas/preview", {
        ...payload,
        cargador_contractual: puedeElegirCargador ? cargadorContractual : "empresa",
        overrides: buildOverrides(),
      });
      setPreview(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setPreviewError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (payload) load(); }, [payload, cargadorContractual]); // eslint-disable-line

  useEffect(() => {
    if (fechaEditada.current || !preview?.grupos?.length) return;
    const fromApi = String(preview.grupos[0].fecha_transporte || preview.grupos[0].fecha_albaran || "").slice(0, 10);
    setFecha(fechaTransportePorDefecto(fromApi));
  }, [preview]);

  useEffect(() => {
    api.get("/api/v1/decas/manual/opciones")
      .then(({ data }) => setCatalogoTra((prev) => mergeTraCatalogo(data.transportistas || [], prev)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!preview?.grupos) return;
    setCatalogoTra((prev) => mergeTraCatalogo(prev, preview.grupos.map((g) => g.transportista)));
    setTraPorGrupo((prev) => {
      const next = { ...prev };
      preview.grupos.forEach((g, i) => {
        if (next[i] != null) return;
        const t = g.transportista || {};
        if (claveTraSelect(t)) next[i] = t;
      });
      return next;
    });
    setTraLibre((prev) => {
      const next = { ...prev };
      preview.grupos.forEach((g, i) => {
        if (next[i] != null) return;
        const t = g.transportista || {};
        const nom = (t.razon_social || "").trim();
        next[i] = !((t.nif || "").trim()) || !nom || nom === "-";
      });
      return next;
    });
    setVehiculosPorNif((prev) => {
      if (!sageCon) return prev;
      const next = { ...prev };
      preview.grupos.forEach((g) => {
        const nif = String(g.transportista?.nif || "").trim().toUpperCase();
        const mats = g.matriculas || [];
        if (nif && mats[0] && !next[nif]) next[nif] = mats[0];
      });
      return next;
    });
    setRemolquesPorNif((prev) => {
      if (!sageCon) return prev;
      const next = { ...prev };
      preview.grupos.forEach((g) => {
        const nif = String(g.transportista?.nif || "").trim().toUpperCase();
        const mats = g.matriculas || [];
        if (nif && mats[1] && !next[nif]) next[nif] = mats[1];
      });
      return next;
    });
    setConductoresPorNif((prev) => {
      if (!sageCon) return prev;
      const next = { ...prev };
      preview.grupos.forEach((g) => {
        const nif = String(g.transportista?.nif || "").trim().toUpperCase();
        const cond = String(g.conductor || "").trim();
        if (nif && cond && !next[nif]) next[nif] = cond;
      });
      return next;
    });
    setEnviosEdit((prev) => {
      const next = { ...prev };
      preview.grupos.forEach((g) => {
        (g.envios || []).forEach((e) => {
          const k = claveEnvioAlb(e);
          if (!next[k]) {
            next[k] = { bultos: e.bultos ?? "", peso_bruto: e.peso_bruto ?? "", dest_id: "sage", orig_id: "sage" };
          } else {
            if (next[k].dest_id == null) next[k] = { ...next[k], dest_id: "sage" };
            if (next[k].orig_id == null) next[k] = { ...next[k], orig_id: "sage" };
          }
        });
      });
      return next;
    });
  }, [preview, sageCon]);

  const resumenBloqueos = useMemo(() => {
    if (!preview?.grupos) return [];
    const rows = [];
    preview.grupos.forEach((g, i) => {
      (g.bloqueos || []).forEach((b) => {
        rows.push({
          grupo: i + 1,
          albaran: fmtAlbaranNro(b.albaran?.serie, b.albaran?.numero),
          campos: (b.faltantes || []).map(etiquetaCampo),
        });
      });
    });
    return rows;
  }, [preview]);

  const resumenAvisos = useMemo(() => {
    if (!preview?.grupos) return [];
    const rows = [];
    preview.grupos.forEach((g, i) => {
      (g.warnings || []).forEach((b) => {
        rows.push({
          grupo: i + 1,
          albaran: fmtAlbaranNro(b.albaran?.serie, b.albaran?.numero),
        });
      });
    });
    return rows;
  }, [preview]);

  const nifGrupo = (g, i) => (
    (traPorGrupo[i]?.nif || g.transportista?.nif || "")
  ).toUpperCase();
  const nifsGrupos = (preview?.grupos || [])
    .map((g, i) => nifGrupo(g, i))
    .filter(Boolean);
  const faltaMatricula = nifsGrupos.some((nif) => !(vehiculosPorNif[nif] || "").trim());
  const faltaTra = (g, i) => {
    const t = traPorGrupo[i] || g.transportista || {};
    const nom = (t.razon_social || "").trim();
    return !((t.nif || "").trim()) || !nom || nom === "-";
  };

  const generar = async (comoBorrador = false) => {
    if (!comoBorrador && !preview?.puede_generar) {
      toast.error(preview?.no_encontrados?.length
        ? "Hay albaranes que no se han podido cargar"
        : "Corrige los bloqueos antes de generar");
      return;
    }
    if (comoBorrador && !(preview?.puede_borrador || preview?.puede_generar)) {
      toast.error(preview?.no_encontrados?.length
        ? "Hay albaranes que no se han podido cargar"
        : "Corrige los bloqueos antes de guardar el borrador");
      return;
    }
    if (faltaMatricula && !comoBorrador) {
      setAskBorrador(true);
      return;
    }
    setLoading(true);
    try {
      const overrides = buildOverrides({
        conductores_por_transportista: Object.fromEntries(
          Object.entries(conductoresPorNif).filter(([, v]) => v)
        ),
        matriculas_por_transportista: matriculasPorNif(vehiculosPorNif, remolquesPorNif),
      });
      const { generados, recuperado } = await postGenerarConRecuperacion(
        "/api/v1/decas/generar",
        {
          ...payload,
          cargador_contractual: puedeElegirCargador ? cargadorContractual : "empresa",
          overrides,
          borrador: !!comoBorrador,
        },
        { fuente: (payload.albaranes || []).every((a) => a.fuente === "manual") ? "manual" : "sage" },
      );
      if (recuperado) {
        toast.info("El servidor tard" + String.fromCharCode(0xf3) + ", pero el DeCA s" + String.fromCharCode(0xed) + " se emiti" + String.fromCharCode(0xf3) + ".");
      } else if (comoBorrador || generados.some((g) => g.estado === "borrador")) {
        toast.success("Guardado. Completa la matr" + String.fromCharCode(0xed) + "cula en el detalle para emitir el DeCA oficial.");
      } else {
        toast.success(`${generados.length} DeCA generado(s)`);
      }
      setAskBorrador(false);
      sessionStorage.removeItem("deca_preview_payload");
      liberar();
      irAGenerados(nav, generados, { abrirEmail: !generados.some((g) => g.estado === "borrador") });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!payload) return null;

  return (
    <div>
      <AvisoSalida blocker={blocker} saliendo={saliendo} confirmar={confirmar} cancelar={cancelar} />
      <ConfirmarBorrador
        open={askBorrador}
        onOpenChange={setAskBorrador}
        busy={loading}
        onConfirm={() => generar(true)}
      />
      <PageHeader
        title="Previsualización de agrupación"
        subtitle={`${payload.tipo_deca === "proveedor" ? "DeCA a proveedor (origen proveedor, destino empresa)" : (payload.modo === "agrupado" ? "Por destino (mismo cliente = un DeCA)" : "Un solo DeCA con todos los albaranes")} · ${payload.albaranes.length} albaranes seleccionados`}
        testid="page-preview"
        actions={
          <>
            <Button variant="outline" onClick={()=>nav("/albaranes")}><ChevronLeft className="h-4 w-4 mr-2"/> Volver</Button>
            <Button variant="outline" onClick={() => generar(true)} disabled={loading || !(preview?.puede_borrador || preview?.puede_generar)} data-testid="btn-borrador">
              Guardar borrador
            </Button>
            <Button onClick={() => generar(false)} disabled={loading || !preview?.puede_generar} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-generar">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <FilePlus2 className="h-4 w-4 mr-2"/>} Generar DeCA
            </Button>
          </>
        }
      />

      {payload.tipo_deca === "proveedor" ? (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-sm text-blue-900" data-testid="preview-tipo-proveedor">
          <span className="font-semibold">DeCA a proveedor.</span>{" "}
          Origen: {payload.proveedor?.razon_social || "proveedor"}. Destino: cargador de la empresa.
        </div>
      ) : user?.cargador_es_cliente ? (
        <Card className="p-3 mb-4" data-testid="preview-cargador-contractual">
          <div className="label-tag mb-1">Cargador contractual</div>
          <Tabs value={cargadorContractual} onValueChange={setCargadorContractual}>
            <TabsList data-testid="preview-tipo-cargador">
              <TabsTrigger value="empresa">Esta empresa</TabsTrigger>
              <TabsTrigger value="cliente">El cliente</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-xs text-slate-500 mt-2">
            {cargadorContractual === "cliente"
              ? "El cargador del DeCA ser" + String.fromCharCode(0xe1) + " el cliente. El origen de carga sigue siendo el de la empresa."
              : "El cargador contractual es el de esta empresa, como de costumbre."}
          </p>
        </Card>
      ) : null}

      {/* Overrides card */}
      <Card className="p-4 mb-4">
        <div className="label-tag mb-3">Datos de transporte (aplicables a todos los grupos)</div>
        <div className="grid md:grid-cols-4 gap-3">
          <Field label={"Autorizaci" + String.fromCharCode(0xf3) + "n especial"}>
            <Input value={autorizacion} maxLength={LEN.autorizacion} onChange={(e)=>setAutorizacion(e.target.value)} placeholder="ADR / especial" data-testid="in-autorizacion"/>
          </Field>
          <div>
            <Field label="Fecha transporte">
              <DatePicker value={fecha} onChange={(v) => { fechaEditada.current = true; setFecha(v); }} testid="in-fecha"/>
            </Field>
            <div className="text-[11px] text-slate-500 mt-1">
              Si la fecha del albar{String.fromCharCode(0xe1)}n es anterior a hoy, se usa la de hoy. Si es hoy o posterior, se deja la del albar{String.fromCharCode(0xe1)}n.
            </div>
          </div>
          <div>
            <Field label="Fecha de descarga">
              <DatePicker value={fechaDescarga} onChange={setFechaDescarga} testid="in-fecha-descarga"/>
            </Field>
            <div className="text-[11px] text-slate-500 mt-1">
              Estimada de fin de transporte. Si est{String.fromCharCode(0xe1)} vac{String.fromCharCode(0xed)}a no sale en el PDF.
            </div>
          </div>
          <Field className="md:col-span-4" label="Observaciones / reservas">
            <Textarea value={observaciones} maxLength={LEN.observaciones} onChange={(e)=>setObservaciones(e.target.value)} rows={2} data-testid="in-obs"/>
          </Field>
        </div>
        <div className="flex justify-end mt-3">
          <Button variant="outline" onClick={load} data-testid="btn-preview-refresh">Aplicar y recalcular</Button>
        </div>
      </Card>

      {preview ? (
        <>
          <div className="mb-4 space-y-3">
            {preview.no_encontrados?.length ? (
              <Alert className="border-red-200 bg-red-50 text-red-800 w-full" data-testid="alert-no-encontrados">
                <ShieldAlert className="h-4 w-4"/>
                <AlertTitle>
                  {preview.no_encontrados.length === 1
                    ? "1 albar" + String.fromCharCode(0xe1) + "n no encontrado"
                    : preview.no_encontrados.length + " albaranes no encontrados"}
                </AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    Seleccionaste {payload.albaranes.length} y no se pudo cargar {preview.no_encontrados.length}. Si es un albar{String.fromCharCode(0xe1)}n manual, debe existir en DeCA. Si es de Sage, revisa ejercicio, serie y n{String.fromCharCode(0xfa)}mero.
                  </p>
                  <ul className="text-sm space-y-1 list-none p-0 m-0">
                    {preview.no_encontrados.map((a, k) => (
                      <li key={k} className="font-mono">{etiquetaRef(a)}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
            {preview.puede_generar ? (
              <>
              <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800" data-testid="alert-ok">
                <CheckCircle2 className="h-4 w-4"/>
                <AlertTitle>Listo para generar {preview.num_grupos} DeCA</AlertTitle>
                <AlertDescription>
                  {faltaMatricula
                    ? "Falta la matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo en al menos un grupo. Puedes indicarla ahora o guardar un borrador y emitir el DeCA cuando la tengas."
                    : "Todos los env" + String.fromCharCode(0xed) + "os cumplen los campos obligatorios del art. 6."}
                </AlertDescription>
              </Alert>
              {resumenAvisos.length ? (
                <Alert className="border-amber-200 bg-amber-50 text-amber-900 mt-3" data-testid="alert-reutilizar">
                  <AlertTriangle className="h-4 w-4"/>
                  <AlertTitle>Albaranes ya usados en otro DeCA</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">{AVISO_REUTILIZAR}</p>
                    <ul className="text-sm space-y-1 list-none p-0 m-0">
                      {resumenAvisos.map((r, k) => (
                        <li key={k}>
                          <span className="font-semibold">DeCA #{r.grupo}</span>
                          {" · albarán "}
                          <span className="font-mono">{r.albaran}</span>
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}
              </>
            ) : resumenBloqueos.length ? (
              <Alert className="border-red-200 bg-red-50 text-red-800 w-full" data-testid="alert-block">
                <ShieldAlert className="h-4 w-4"/>
                <AlertTitle>No se puede generar</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">Faltan datos del art. 6. Puedes completar bultos, kilos y transportista aqu{String.fromCharCode(0xed)} (solo para el DeCA, no se env{String.fromCharCode(0xed)}a a Sage) y pulsar Aplicar y recalcular.</p>
                  <ul className="text-sm space-y-1.5 list-none p-0 m-0">
                    {resumenBloqueos.map((r, k) => (
                      <li key={k}>
                        <span className="font-semibold">DeCA #{r.grupo}</span>
                        {" · albarán "}
                        <span className="font-mono">{r.albaran}</span>
                        {": "}
                        {r.campos.join("; ")}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : preview.no_encontrados?.length ? null : (
              <Alert className="border-red-200 bg-red-50 text-red-800 w-full" data-testid="alert-block">
                <ShieldAlert className="h-4 w-4"/>
                <AlertTitle>No se puede generar</AlertTitle>
                <AlertDescription>Revisa los grupos marcados más abajo.</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="grid gap-4">
            {preview.grupos.map((g, i) => (
              <Card key={i} className={`p-4 ${g.bloqueos.length ? "border-red-300" : (g.warnings || []).length ? "border-amber-300" : ""}`} data-testid={`grupo-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="label-tag">DeCA #{i + 1}</div>
                    <h3 className="text-xl font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "Chivo" }}>
                      <Truck className="h-5 w-5 inline mr-2 text-blue-700"/>
                      {g.destinatario?.razon_social || g.envios?.[0]?.destinatario_nombre || "Destino"}
                    </h3>
                    <div className="text-xs text-slate-500 mt-1">
                      {g.cargador.razon_social || "—"} <span className="text-slate-400">→</span> {(traPorGrupo[i]?.razon_social || g.transportista.razon_social) || "sin transportista"}
                    </div>
                    <div className="text-xs font-mono text-slate-500 mt-1">
                      {g.clave?.destinatario_nif || g.destinatario?.nif || "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-700" style={{ fontFamily: "Chivo" }}>{g.envios.length}</div>
                    <div className="label-tag">{palabraEnvio(g.envios.length)}</div>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-3 mt-4">
                  <KV label="Cargador NIF" value={g.cargador.nif} mono />
                  <KV label="Transportista NIF" value={traPorGrupo[i]?.nif || g.transportista.nif} mono />
                  <KV label="Fecha transporte" value={fmtFecha(fecha || g.fecha_transporte)} />
                  {fechaDescarga || g.fecha_descarga ? (
                    <KV label="Fecha de descarga" value={fmtFecha(fechaDescarga || g.fecha_descarga)} />
                  ) : null}
                  {g.fecha_albaran && fecha && g.fecha_albaran.slice(0, 10) !== fecha ? (
                    <KV label={"Fecha albar" + String.fromCharCode(0xe1) + "n (Sage)"} value={fmtFecha(g.fecha_albaran)} />
                  ) : null}
                  <KV label="Matrículas" value={(g.matriculas || []).join(", ") || "—"} mono />
                </div>
                {traLibre[i] || faltaTra(g, i) ? (
                  <div className="mt-3 max-w-xl">
                    <Label className="label-tag">Transportista (solo DeCA)</Label>
                    <Select
                      value={claveTraSelect(traPorGrupo[i]) || "none"}
                      onValueChange={(v) => {
                        if (v === "none") {
                          setTraPorGrupo((prev) => {
                            const n = { ...prev };
                            delete n[i];
                            return n;
                          });
                          return;
                        }
                        const t = catalogoTra.find((x) => claveTraSelect(x) === v);
                        setTraPorGrupo((prev) => ({ ...prev, [i]: t || { nif: "", razon_social: v } }));
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid={`tra-grupo-${i}`}>
                        <SelectValue placeholder="Selecciona transportista"/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin transportista</SelectItem>
                        {catalogoTra.filter((t) => claveTraSelect(t)).map((t) => (
                          <SelectItem key={claveTraSelect(t)} value={claveTraSelect(t)}>{etiquetaTraSelect(t)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-[11px] text-slate-500 mt-1">
                      No se modifica Sage; solo se usa en este DeCA.
                    </div>
                  </div>
                ) : null}
                <div className="grid md:grid-cols-2 gap-3 mt-3 max-w-2xl">
                  <MatriculaSelect
                    transportistaNif={nifGrupo(g, i)}
                    transportistaRazon={traPorGrupo[i]?.razon_social || g.transportista?.razon_social}
                    transportistaCodigo={traPorGrupo[i]?.codigo || g.transportista?.codigo}
                    tipo="vehiculo"
                    required
                    value={vehiculosPorNif[nifGrupo(g, i)] || ""}
                    onChange={(v) => setVehiculosPorNif((prev) => ({ ...prev, [nifGrupo(g, i)]: v }))}
                    testid={`vehiculo-grupo-${i}`}
                  />
                  <MatriculaSelect
                    transportistaNif={nifGrupo(g, i)}
                    transportistaRazon={traPorGrupo[i]?.razon_social || g.transportista?.razon_social}
                    transportistaCodigo={traPorGrupo[i]?.codigo || g.transportista?.codigo}
                    tipo="remolque"
                    value={remolquesPorNif[nifGrupo(g, i)] || ""}
                    onChange={(v) => setRemolquesPorNif((prev) => ({ ...prev, [nifGrupo(g, i)]: v }))}
                    testid={`remolque-grupo-${i}`}
                  />
                  <ConductorSelect
                    transportistaNif={nifGrupo(g, i)}
                    transportistaRazon={traPorGrupo[i]?.razon_social || g.transportista?.razon_social}
                    transportistaCodigo={traPorGrupo[i]?.codigo || g.transportista?.codigo}
                    value={conductoresPorNif[nifGrupo(g, i)] || ""}
                    onChange={(v) => setConductoresPorNif((prev) => ({ ...prev, [nifGrupo(g, i)]: v }))}
                    testid={`conductor-grupo-${i}`}
                  />
                </div>

                <div className="mt-4 overflow-x-auto">
                  <EnviosSortableTable
                    envios={g.envios}
                    rowKey={(e) => e.orden}
                    editValues={enviosEdit}
                    onEditEnvio={(e, patch) => {
                      const k = claveEnvioAlb(e);
                      setEnviosEdit((prev) => ({
                        ...prev,
                        [k]: { bultos: prev[k]?.bultos ?? e.bultos ?? "", peso_bruto: prev[k]?.peso_bruto ?? e.peso_bruto ?? "", dest_id: prev[k]?.dest_id || "sage", orig_id: prev[k]?.orig_id || "sage", ...patch },
                      }));
                    }}
                    renderOrigen={(e) => {
                      const k = claveEnvioAlb(e);
                      const ov = enviosEdit[k] || {};
                      const car = g.cargador_empresa || g.cargador || {};
                      if (payload.tipo_deca === "proveedor") {
                        return <div className="min-w-[14rem] text-sm">{ov.origen || e.origen}</div>;
                      }
                      return (
                        <div className="min-w-[16rem]">
                          <DestinoClienteSelect
                            hideLabel
                            modo="cargador"
                            clienteNif={car.nif}
                            clienteDir={car}
                            sageLabel={e.origen && e.origen !== "-" ? e.origen : ""}
                            valueId={ov.orig_id || "sage"}
                            testid={`envio-orig-${k}`}
                            onSelect={(sel) => {
                              setEnviosEdit((prev) => ({
                                ...prev,
                                [k]: {
                                  ...prev[k],
                                  bultos: prev[k]?.bultos ?? e.bultos ?? "",
                                  peso_bruto: prev[k]?.peso_bruto ?? e.peso_bruto ?? "",
                                  dest_id: prev[k]?.dest_id || "sage",
                                  orig_id: sel.id || "sage",
                                  origen: sel.id === "sage" ? undefined : (sel.texto || fmtDomicilio(sel) || ""),
                                  orig_domicilio: sel.domicilio,
                                  orig_municipio: sel.municipio,
                                  orig_cp: sel.cp,
                                  orig_provincia: sel.provincia,
                                },
                              }));
                            }}
                          />
                        </div>
                      );
                    }}
                    renderDestino={(e) => {
                      const k = claveEnvioAlb(e);
                      const ov = enviosEdit[k] || {};
                      if (payload.tipo_deca === "proveedor") {
                        return (
                          <div className="min-w-[16rem]">
                            <div className="font-medium">{e.destinatario_nombre}</div>
                            <div className="text-xs text-slate-500">{e.destino}</div>
                          </div>
                        );
                      }
                      return (
                        <div className="min-w-[16rem]">
                          <div className="font-medium">{e.destinatario_nombre}</div>
                          <DestinoClienteSelect
                            hideLabel
                            clienteNif={e.destinatario_nif}
                            sageLabel={e.destino}
                            valueId={ov.dest_id || "sage"}
                            testid={`envio-dest-${k}`}
                            onSelect={(sel) => {
                              setEnviosEdit((prev) => ({
                                ...prev,
                                [k]: {
                                  ...prev[k],
                                  bultos: prev[k]?.bultos ?? e.bultos ?? "",
                                  peso_bruto: prev[k]?.peso_bruto ?? e.peso_bruto ?? "",
                                  dest_id: sel.id || "sage",
                                  destino: sel.id === "sage" ? undefined : sel.texto,
                                  destinatario_domicilio: sel.domicilio,
                                  destinatario_municipio: sel.municipio,
                                  destinatario_cp: sel.cp,
                                  destinatario_provincia: sel.provincia,
                                },
                              }));
                            }}
                          />
                        </div>
                      );
                    }}
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Bultos, kilos, origen y domicilio extra se guardan solo en el DeCA; Sage no se actualiza.
                  </div>
                </div>

                {(g.warnings || []).length > 0 ? (
                  <div className="mt-3 border border-amber-200 bg-amber-50 rounded-md p-3" data-testid={`avisos-${i}`}>
                    <div className="text-sm font-semibold text-amber-900 mb-1">Aviso</div>
                    <ul className="text-xs text-amber-800 space-y-0.5">
                      {g.warnings.map((b, k) => (
                        <li key={k}>Albarán <b>{fmtAlbaranNro(b.albaran.serie, b.albaran.numero)}</b>: {AVISO_REUTILIZAR}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {g.bloqueos.length > 0 ? (
                  <div className="mt-3 border border-red-200 bg-red-50 rounded-md p-3" data-testid={`bloqueos-${i}`}>
                    <div className="text-sm font-semibold text-red-800 mb-1">Bloqueos art. 6</div>
                    <ul className="text-xs text-red-700 space-y-0.5">
                      {g.bloqueos.map((b, k) => (
                        <li key={k}>Albarán <b>{fmtAlbaranNro(b.albaran.serie, b.albaran.numero)}</b>: {(b.faltantes || []).map(etiquetaCampo).join("; ")}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        </>
      ) : previewError ? (
        <Alert className="border-red-200 bg-red-50 text-red-800 w-full" data-testid="alert-preview-error">
          <ShieldAlert className="h-4 w-4"/>
          <AlertTitle>No se pudo previsualizar</AlertTitle>
          <AlertDescription>{previewError}</AlertDescription>
        </Alert>
      ) : (
        <div className="py-12 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2"/> Calculando agrupación…</div>
      )}
    </div>
  );
}
